import React, { useState } from "react";
import { useParams } from "react-router-dom";
import PageHeader from "../../components/common/PageHeader";
import ExperienceHeroSection from "../../components/UserComponents/ExperienceHeroSection";
import ExperienceInfo from "../../components/UserComponents/ExperienceInfo";
import VendorInfo from "../../components/UserComponents/VendorInfo";
import StatusBadge from "../../components/common/StatusBadge";
import CustomButton from "../../components/common/CustomButton";
import { toast } from "react-toastify";
import {
  useAdminExperienceById,
  useAdminExperienceApprove,
  useAdminExperienceReject,
} from "../../custom-hooks/queries";
import RejectionModal from "../../components/Modals/RejectionModal";
import { formatDate, getCleanedUrl } from "../../utils/HelperMethod";

export default function AdminExperienceDetails() {
  const { experienceId } = useParams();

  const [showRejectModal, setShowRejectModal] = useState(false);

  /* 🔹 Queries & Mutations (TOP LEVEL – rules follow ho rahe hain) */
  const { data, isLoading, isError, refetch } =
    useAdminExperienceById(experienceId);
  const { mutate: approveMutation, isPending: isApproving } =
    useAdminExperienceApprove();
  const { mutate: rejectMutation, isPending: isRejecting } =
    useAdminExperienceReject();

  const experience = data?.data?.data || {};

  const expStatus = experience?.status;

  const vendorLogo = getCleanedUrl(experience.vendor?.logo);

  if (isLoading) {
    return <div className="p-10 text-center">Loading...</div>;
  }

  if (isError || !experience?.experienceId) {
    return (
      <div className="p-10 text-center text-red-500">Experience not found</div>
    );
  }

  return (
    <div className="container mx-auto py-1 px-6">
      <PageHeader title="Experience Details" />

      {/* 🔹 HERO SECTION (UNCHANGED) */}
      <ExperienceHeroSection
        heroImage={experience.images?.[0]}
        galleryImages={experience.images || []}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-10 mt-10 items-start">
        {/* LEFT SIDE */}
        <div className="lg:col-span-2 space-y-6 w-full">
          <ExperienceInfo
            title={experience.title}
            rating={experience?.rating}
            reviewscount={experience?.totalReviews}
            location={experience.location}
            price={experience.price}
            duration={experience.duration}
            category={experience.category}
            description={experience.description}
            priceType={experience.priceType}
          />

          {experience.vendor && (
            <VendorInfo
              name={experience.vendor.vendorName}
              description={experience.vendor.description}
              rating={experience.vendor.rating}
              logoUrl={vendorLogo}
              totalReviews={experience.vendor.totalReviews}
            />
          )}
        </div>

        {/* RIGHT SIDE — FIXED WIDTH */}
        <div className="w-full lg:col-span-1">
          <div className="bg-white border border-gray-200 rounded-2xl p-4 sm:p-6 h-fit space-y-5 w-full">
            {/* 🔹 Availability */}
            <div className="p-3 sm:p-4 space-y-3">
              <h4 className="font-semibold text-sm sm:text-base">
                Availability
              </h4>

              {experience.availability?.length > 0 ? (
                experience.availability
                  .sort((a, b) => new Date(a.date) - new Date(b.date))
                  .map((day, i) => (
                    <div key={i} className="border rounded-lg p-3 sm:p-4">
                      <p className="text-sm font-medium text-gray-900">
                        {`${formatDate(day.startDate)} - ${formatDate(day?.endDate)}`}
                      </p>

                      <div className="flex flex-wrap gap-2 mt-2">
                        {day?.timeSlots?.map((slot, idx) => (
                          <span
                            key={idx}
                            className="px-3 py-1 rounded-full text-xs bg-[#F5F5F5] whitespace-nowrap"
                          >
                            {slot.startTime} - {slot.endTime}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))
              ) : (
                <p className="text-sm text-gray-500">No availability</p>
              )}
            </div>

            <hr className="my-4 sm:my-6" />

            {/* 🔹 NOTES */}
            {experience?.importantNotes && (
              <div>
                <h2 className="text-base sm:text-lg font-semibold mb-2 sm:mb-4">
                  Notes
                </h2>
                <p className="text-sm text-gray-700">
                  • {experience.importantNotes}
                </p>
              </div>
            )}

            {/* 🔹 ACTION BUTTONS */}
            <div className="flex flex-col sm:flex-row gap-3 pt-2 sm:pt-4">
              <CustomButton
                variant="secondary"
                onClick={() => setShowRejectModal(true)}
                fullWidth
              >
                Reject
              </CustomButton>

              {expStatus?.toLowerCase() !== "approved" && (
                <CustomButton
                  variant="primary"
                  onClick={() =>
                    approveMutation(experience.experienceId, {
                      onSuccess: () => {
                        toast.success("Experience approved successfully!");
                        refetch();
                      },
                    })
                  }
                  disabled={isApproving}
                  loading={isApproving}
                  fullWidth
                >
                  Approve
                </CustomButton>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 🔻 REJECTION MODAL (REUSED – AS REQUESTED) */}
      {showRejectModal && (
        <RejectionModal
          title="Reject Experience"
          description="Please provide a reason for rejecting this experience."
          confirmText="Reject Experience"
          mutationFn={rejectMutation}
          payload={{
            experienceId: experience.experienceId,
          }}
          onClose={() => setShowRejectModal(false)}
          loading={isRejecting}
        />
      )}
    </div>
  );
}
